import React, { FC } from 'react';
import { Modal } from 'antd';
import copy from 'copy-to-clipboard';
import { DataTable } from 'common/components/Table/DataTable';
import { Close, Copy } from 'common/svg';
import { priceFormatter } from 'common/helpers/format.helper';
import { dateFormatter } from 'common/helpers/date.helper';
import {
  ITrackStoreOrderDetailItem,
  ITrackStoreOrderDetails,
  trackStoreOrderItemsConfig,
} from 'entities/TrackStore/TrackStore.models';
import { TrackStoreOrderStatus } from 'entities/TrackStore/components/TrackStoreOrderStatus';
import { trackStoreTransactionItFormatter } from 'entities/TrackStore/TrackStore.helper';

interface IComponentProps {
  open: boolean;
  orderDetails: ITrackStoreOrderDetails | null;
  onCancel: () => void;
}

export const TrackStoreOrderDetailsModal: FC<IComponentProps> = ({ open, orderDetails, onCancel }) => {
  const dataSource = orderDetails ? orderDetails.items.map((item, index) => ({ ...item, key: index })) : [];

  const renderItem = (label: string, value: string): React.ReactNode => {
    return (
      <div>
        <div className="mb-2 ff-regular fs-xxxs color-gray-1">{label}</div>
        <span className="ff-regular fs-xxs">{value}</span>
      </div>
    );
  };

  const copyTransactionId = (e: React.MouseEvent): void => {
    e.stopPropagation();

    if (orderDetails) {
      copy(orderDetails.transactionId);
    }
  };

  return (
    <Modal
      open={open}
      rootClassName="modal"
      width={720}
      height={520}
      centered
      title="Order Information"
      closeIcon={<Close className="icon-gray" />}
      classNames={{ body: 'scrollbar-y' }}
      onCancel={onCancel}
      footer={null}
    >
      <div className="mb-2 grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div>
          <div className="mb-7">
            <div className="mb-5 ff-medium fs-xxs">Store Details</div>

            <div className="mb-4">{renderItem('Store', orderDetails?.store ?? '')}</div>

            {renderItem('Crew Name', orderDetails?.crewName ?? '')}
          </div>

          <div>
            <div className="mb-7 ff-medium fs-xxs">Customer Details</div>

            <div className="mb-4">{renderItem('Customer', orderDetails?.customer ?? '')}</div>

            {renderItem('Phone', orderDetails?.phone ?? '')}
          </div>
        </div>

        <div>
          <div className="mb-5 ff-medium fs-xxs">Order Details</div>

          <div className="mb-4">{renderItem('Items', orderDetails ? `${orderDetails?.items.length}` : '')}</div>

          <div className="mb-4">{renderItem('Total', priceFormatter(orderDetails?.total ?? 0))}</div>

          <div className="mb-4">
            {renderItem('Ordered', dateFormatter(orderDetails?.orderedAt, 'MM/DD/YYYY HH:mm:ss'))}
          </div>

          <div className="mb-4">
            {renderItem('Served', dateFormatter(orderDetails?.servedAt, 'MM/DD/YYYY HH:mm:ss'))}
          </div>

          {renderItem('Channel', orderDetails?.channel ?? '')}
        </div>

        <div>
          <div className="mb-5 ff-medium fs-xxs">Payment Details</div>

          <div className="mb-4">{renderItem('Payment', orderDetails?.payment ?? '')}</div>

          <div className="mb-4">{renderItem('Discount', priceFormatter(orderDetails?.discount ?? 0))}</div>

          <div className="mb-4">{renderItem('Sub Total', priceFormatter(orderDetails?.subTotal ?? 0))}</div>

          <div className="mb-4">{renderItem('Paid', priceFormatter(orderDetails?.paid ?? 0))}</div>

          <div>{renderItem('Change', priceFormatter(orderDetails?.change ?? 0))}</div>
        </div>

        <div className="flex flex__column flex-align-end">
          <div className="mb-5">
            <TrackStoreOrderStatus isOverdue={orderDetails?.isOverdue} />
          </div>

          {orderDetails?.transactionId && (
            <div className="flex">
              <span className="ff-medium fs-xxs">{trackStoreTransactionItFormatter(orderDetails.transactionId)}</span>
              <Copy className="icon-gray" onClick={copyTransactionId} />
            </div>
          )}
        </div>
      </div>

      <DataTable<ITrackStoreOrderDetailItem> dataSource={dataSource} config={trackStoreOrderItemsConfig} />
    </Modal>
  );
};
